import React, { useState, useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { userIDAtom } from '../../store/atoms';
import {
  signUp,
  confirmSignUp,
  signIn,
  signOut,
  getCurrentUser,
  resendSignUpCode
} from 'aws-amplify/auth';
import '../../aws-config';
import '../../App.css';
import DashboardPage from '../landingpages/DashboardPage';
import Signup from './Signup';
import {
  DEMO_AUTH_MODE,
  DEMO_SESSION_KEY,
  DEMO_USERS,
  createDemoSession,
  readDemoSession,
} from '../../config/demoAuth';

function SimpleAuthApp() {
  const [currentPage, setCurrentPage] = useState('login');
  const [userID, setUserID] = useRecoilState(userIDAtom);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageTransition, setPageTransition] = useState(false);

  // Check authentication state on app load
  useEffect(() => {
    checkAuthState();
  }, []);

  // Page transition helper
  const navigateToPage = (page) => {
    setPageTransition(true);
    setTimeout(() => {
      setCurrentPage(page);
      setPageTransition(false);
    }, 300);
  };

  const checkAuthState = async () => {
    if (DEMO_AUTH_MODE) {
      const session = readDemoSession();
      if (session) {
        setUser(session);
        setUserID(session.username);
        setCurrentPage('dashboard');
      } else {
        setUser(null);
        setCurrentPage('login');
      }
      setLoading(false);
      return;
    }

    try {
      const authenticatedUser = await getCurrentUser();
      console.log(' [MindyKnows] User authenticated:', authenticatedUser.username);
      setUser(authenticatedUser);
      setCurrentPage('dashboard');
    } catch (error) {
      console.log('ℹ [MindyKnows] No authenticated user found');
      setUser(null);
      setCurrentPage('login');
    } finally {
      setLoading(false);
    }
  };

  // AWS Cognito Login Component
  const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');

    const handleLogin = async (e) => {
      e.preventDefault();
      setIsLoading(true);
      setMessage('');

      try {
        if (DEMO_AUTH_MODE) {
          const normalizedEmail = email.trim().toLowerCase();
          const matchedUser = DEMO_USERS.find(
            (u) => u.email === normalizedEmail && u.password === password
          );
          if (!matchedUser) {
            setMessage('Invalid email or password.');
            return;
          }

          const demoSession = createDemoSession(matchedUser);
          localStorage.setItem(DEMO_SESSION_KEY, JSON.stringify(demoSession));
          setUserID(demoSession.username);
          setUser(demoSession);
          navigateToPage('dashboard');
          return;
        }

        // console.log('🔐 [MindyKnows] Attempting to sign in:', email);

        const { isSignedIn, nextStep } = await signIn({
          username: email,
          password: password
        });
        setUserID(email); // Store user ID in Recoil state
        console.log('🔍 [MindyKnows] Sign in result:', { isSignedIn, nextStep });

        if (isSignedIn) {
          console.log(' [MindyKnows] Sign in successful');
          const authenticatedUser = await getCurrentUser();
          setUser(authenticatedUser);
          navigateToPage('dashboard');
        } else {
          console.log('⚠️ [MindyKnows] Sign in incomplete, nextStep:', nextStep);
          setMessage('Sign in requires additional steps. Please check your account.');
        }
      } catch (error) {
        console.error('❌ [MindyKnows] Sign in failed:', error);
        setMessage(error.message || 'Sign in failed. Please check your credentials.');
      } finally {
        setIsLoading(false);
      }
    };

    return (
  <div
    className="auth-container"
    style={{
      backgroundImage: `url('/gradientBG2.svg')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}
  >
    {/* Centered frosted glass form card */}
    <div
      style={{
        display: 'flex',
        width: '420px', // smaller width than before
        padding: '20px 24px',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '20px',
        flexShrink: 0,
        borderRadius: '32px',
        background: 'rgba(0, 0, 0, 0.20)',
        backdropFilter: 'blur(30px)',
      }}
    >
      {/* Logo inside the card */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '4px',
        }}
      >
        <img
          src="/logoMK.png"
          alt="MindyKnows Logo"
          style={{
            width: '110px',
            height: 'auto',
          }}
        />
      </div>

      {/* Sign-in MK Image Section (smaller size) */}
      <div
        style={{
          display: 'flex',
          width: '140px',
          padding: '4px 20px 0 20px',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: '334.755px',
        }}
      >
        <img
          src="/mindySign.png"
          alt="Sign In Illustration"
          style={{
            width: '130px',
            height: '120px', // reduced height
            flexShrink: 0,
            aspectRatio: '108.46 / 173.74',
            borderRadius: '12px',
          }}
        />
      </div>

      {/* Text content */}
      <div
        style={{
          display: 'flex',
          padding: '10px',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '6px',
        }}
      >
        <h3
          style={{
            color: 'var(--Foundation, #F5F3ED)',
            fontFamily: 'Manrope',
            fontSize: '24px',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: '120%',
            letterSpacing: '-0.54px',
          }}
        >
          Log In
        </h3>
        <p
          style={{
            width: '280px',
            color: 'var(--Foundation-Foundation, #FFF)',
            textAlign: 'center',
            fontFamily: 'Manrope',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 300,
            lineHeight: '120%',
            letterSpacing: '-0.3px',
          }}
        >
          Welcome! <br/>Sign in for a better college experience.
        </p>
      </div>

      {/* Login Form */}
      <form onSubmit={handleLogin} className="auth-form" style={{ width: '100%' }}>
  <style>
    {`
      ::placeholder {
        color: #FFF;
        opacity: 1; /* make sure placeholder is fully white */
      }
    `}
  </style>

  {/* Outer layout for Email, Password, and Button */}
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '24px',
      alignSelf: 'stretch',
    }}
  >
    {/* ===== Email Field Section ===== */}
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-end',
        gap: '12px',
        alignSelf: 'stretch',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '12px',
          alignSelf: 'stretch',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '8px',
            alignSelf: 'stretch',
          }}
        >
          <label
            htmlFor="email"
            style={{
              alignSelf: 'stretch',
              color: '#FFF',
              fontFamily: 'Manrope',
              fontSize: '13px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '120%',
              letterSpacing: '-0.26px',
            }}
          >
            Email Address
          </label>

          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
            style={{
              display: 'flex',
              padding: '16px',
              alignItems: 'center',
              gap: '10px',
              alignSelf: 'stretch',
              borderRadius: '10px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              background: 'rgba(255, 255, 255, 0.1)',
              color: '#FFF',
              fontFamily: 'Manrope',
              fontSize: '14px',
              outline: 'none',
            }}
          />
        </div>
      </div>
    </div>

    {/* ===== Password Field Section ===== */}
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-end',
        gap: '12px',
        alignSelf: 'stretch',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '12px',
          alignSelf: 'stretch',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '8px',
            alignSelf: 'stretch',
          }}
        >
          <label
            htmlFor="password"
            style={{
              alignSelf: 'stretch',
              color: '#FFF',
              fontFamily: 'Manrope',
              fontSize: '13px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '120%',
              letterSpacing: '-0.26px',
            }}
          >
            Password
          </label>

          {/*  Input Wrapper */}
          <div
            style={{
              position: 'relative',
              width: '100%',
            }}
          >
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              style={{
                width: '100%',
                padding: '16px',
                paddingRight: '48px', // extra space for the icon
                borderRadius: '10px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#FFF',
                fontFamily: 'Manrope',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />

            {/*  Passkey Icon */}
            
            {/* <img
              src="/passkey.svg"
              alt="Passkey Icon"
              style={{
                position: 'absolute',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '20px',
                height: '20px',
                aspectRatio: '1 / 1',
                pointerEvents: 'none', // allows clicks to pass through to input
              }}
            /> */}
            <svg 
             style={{
                position: 'absolute',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '20px',
                height: '20px',
                aspectRatio: '1 / 1',
                pointerEvents: 'none', // allows clicks to pass through to input
              }}
              xmlns="http://www.w3.org/2000/svg" width="18" height="14" viewBox="0 0 18 14" fill="none">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M10.2197 0.21967C10.5126 -0.0732233 10.9874 -0.0732233 11.2803 0.21967L17.2803 6.21967C17.5732 6.51256 17.5732 6.98744 17.2803 7.28033L11.2803 13.2803C10.9874 13.5732 10.5126 13.5732 10.2197 13.2803C9.92678 12.9874 9.92678 12.5126 10.2197 12.2197L14.9393 7.5H0.75C0.335786 7.5 0 7.16421 0 6.75C0 6.33579 0.335786 6 0.75 6H14.9393L10.2197 1.28033C9.92678 0.987437 9.92678 0.512563 10.2197 0.21967Z" fill="white"/>
</svg>
          </div>
        </div>
      </div>
    </div>


    {/* ===== Login Button Section ===== */}
    <button
      type="submit"
      disabled={isLoading}
      style={{
        display: 'flex',
        padding: '16px 8px',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        alignSelf: 'stretch',
        borderRadius: '30px',
        background: '#7EB3AB',
        border: 'none',
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '4px',
          alignSelf: 'stretch',
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            color: '#FFF',
            textOverflow: 'ellipsis',
            fontFamily: 'Manrope',
            fontSize: '15px',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: '120%',
            letterSpacing: '-0.3px',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 1,
          }}
        >
          {isLoading ? 'Signing In...' : 'Login'}
        </span>
      </div>
    </button>
          {/* passkey button div  */}
    <div className='keybutton'
     
      style={{
        display: 'flex',
        padding: '16px 8px',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        alignSelf: 'stretch',
        borderRadius: '30px',
        background: '#2E6E87',
        border: 'none',
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '4px',
          color: '#FFF',
          alignSelf: 'stretch',
          fontSize: '15px',
          fontWeight: 700,
          gap: '10px',
        }}
      >
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M9.25 16C9.25 14.4812 10.4812 13.25 12 13.25C13.5188 13.25 14.75 14.4812 14.75 16C14.75 17.5188 13.5188 18.75 12 18.75C10.4812 18.75 9.25 17.5188 9.25 16ZM12 14.75C11.3096 14.75 10.75 15.3096 10.75 16C10.75 16.6904 11.3096 17.25 12 17.25C12.6904 17.25 13.25 16.6904 13.25 16C13.25 15.3096 12.6904 14.75 12 14.75Z" fill="white"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M5.25 9.30277V8C5.25 4.27208 8.27208 1.25 12 1.25C15.7279 1.25 18.75 4.27208 18.75 8V9.30277C18.9768 9.31872 19.1906 9.33948 19.3918 9.36652C20.2919 9.48754 21.0497 9.74643 21.6517 10.3483C22.2536 10.9503 22.5125 11.7081 22.6335 12.6082C22.75 13.4752 22.75 14.5775 22.75 15.9451V16.0549C22.75 17.4225 22.75 18.5248 22.6335 19.3918C22.5125 20.2919 22.2536 21.0497 21.6517 21.6516C21.0497 22.2536 20.2919 22.5125 19.3918 22.6335C18.5248 22.75 17.4225 22.75 16.0549 22.75H7.94513C6.57754 22.75 5.47522 22.75 4.60825 22.6335C3.70814 22.5125 2.95027 22.2536 2.34835 21.6516C1.74643 21.0497 1.48754 20.2919 1.36652 19.3918C1.24996 18.5248 1.24998 17.4225 1.25 16.0549V15.9451C1.24998 14.5775 1.24996 13.4752 1.36652 12.6082C1.48754 11.7081 1.74643 10.9503 2.34835 10.3483C2.95027 9.74643 3.70814 9.48754 4.60825 9.36652C4.80938 9.33948 5.02317 9.31872 5.25 9.30277ZM6.75 8C6.75 5.10051 9.10051 2.75 12 2.75C14.8995 2.75 17.25 5.10051 17.25 8V9.25344C16.8765 9.24999 16.4784 9.24999 16.0549 9.25H7.94513C7.52161 9.24999 7.12353 9.24999 6.75 9.25344V8ZM4.80812 10.8531C4.07435 10.9518 3.68577 11.1322 3.40901 11.409C3.13225 11.6858 2.9518 12.0743 2.85315 12.8081C2.75159 13.5635 2.75 14.5646 2.75 16C2.75 17.4354 2.75159 18.4365 2.85315 19.1919C2.9518 19.9257 3.13225 20.3142 3.40901 20.591C3.68577 20.8678 4.07435 21.0482 4.80812 21.1469C5.56347 21.2484 6.56459 21.25 8 21.25H16C17.4354 21.25 18.4365 21.2484 19.1919 21.1469C19.9257 21.0482 20.3142 20.8678 20.591 20.591C20.8678 20.3142 21.0482 19.9257 21.1469 19.1919C21.2484 18.4365 21.25 17.4354 21.25 16C21.25 14.5646 21.2484 13.5635 21.1469 12.8081C21.0482 12.0743 20.8678 11.6858 20.591 11.409C20.3142 11.1322 19.9257 10.9518 19.1919 10.8531C18.4365 10.7516 17.4354 10.75 16 10.75H8C6.56459 10.75 5.56347 10.7516 4.80812 10.8531Z" fill="white"/>
</svg>
        Sign In with a Passkey
      </div>
    </div>
  </div>

  {/* Error message under form */}
  {message && <div className="message error">{message}</div>}
</form>



      {/* Signup link */}
      {/* <div className="auth-links">
        <p>
          Don’t have an account?{' '}
          <button
            onClick={() => navigateToPage('signup')}
            className="link-button"
          >
            Sign up
          </button>
        </p>
      </div> */}
    </div>
  </div>
);

  };


  // Route to appropriate page 
  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'login':
        return <LoginPage />;   //  Show login page
      case 'signup':
        return <Signup />;      //  Show signup page
      case 'dashboard':
        return <DashboardPage />; // Show dashboard after login
      default:
        return <LoginPage />;
    }
  };

  // Show loading screen during initial auth check
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>🧠 Loading MindyKnows...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {renderCurrentPage()}

      {/* Page Transition Loader */}
      {pageTransition && (
        <div className="page-transition-overlay">
          <div className="pulse-loader">
            <div className="pulse-dot"></div>
            <div className="pulse-dot"></div>
            <div className="pulse-dot"></div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SimpleAuthApp;